import { useInfiniteQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../CommonUtils/httpBodyOptions';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { regroupItems, RegroupedActivitiesDto, UserActivitySortOrder } from '../UserActivity.meta';
import { queryKeys } from './queryKeys';

const filtersFormValue = (ids: string[]) => [...ids].sort().join(',');

interface CancelablePromise extends Promise<RegroupedActivitiesDto> {
  cancel(): void;
}

interface ActivityPageParam {
  lastAuditEventDate: string | null;
  lastAuditEventId: number | null;
  lastManualEventDate: string | null;
  lastManualEventId: number | null;
}

interface GetActivitiesParams {
  clientId: string;
  hideAutomatic: boolean;
  sortOrder: UserActivitySortOrder;
  selectedActivityFilterIds: string[];
  selectedUserFilterIds: string[];
  pageParam?: ActivityPageParam;
}

const getActivities = ({
  clientId,
  hideAutomatic,
  sortOrder,
  selectedActivityFilterIds,
  selectedUserFilterIds,
  pageParam,
}: GetActivitiesParams) => {
  const controller = new AbortController();
  const { signal } = controller;

  const promise = authFetch(urlWithParams('/Consumer/ActivityLog', { clientId }), {
    method: 'POST',
    signal,
    ...httpBodyOptions(
      {
        hideAutomatic,
        sortOrder,
        selectedActivityFilters: filtersFormValue(selectedActivityFilterIds),
        selectedUserFilters: filtersFormValue(selectedUserFilterIds),
        ...(pageParam !== undefined
          ? {
              lastAuditEventDate: pageParam.lastAuditEventDate ?? undefined,
              lastAuditEventId: pageParam.lastAuditEventId ?? undefined,
              lastManualEventDate: pageParam.lastManualEventDate ?? undefined,
              lastManualEventId: pageParam.lastManualEventId ?? undefined,
            }
          : {}),
      },
      false,
    ),
  }).then(regroupItems) as CancelablePromise;

  promise.cancel = () => controller.abort();

  return promise;
};

interface QueryParams {
  clientId: string;
  hideAutomatic: boolean;
  sortOrder: UserActivitySortOrder;
  selectedActivityFilterIds: string[];
  selectedUserFilterIds: string[];
  filtersReady: boolean;
}

export const useActivityQuery = ({
  clientId,
  hideAutomatic,
  sortOrder,
  selectedActivityFilterIds,
  selectedUserFilterIds,
  filtersReady,
}: QueryParams) => {
  return useInfiniteQuery(
    queryKeys.activityLog(
      clientId,
      hideAutomatic,
      sortOrder,
      selectedActivityFilterIds,
      selectedUserFilterIds,
    ),

    ({ pageParam }) =>
      getActivities({
        clientId,
        hideAutomatic,
        sortOrder,
        selectedActivityFilterIds,
        selectedUserFilterIds,
        pageParam,
      }),

    {
      getNextPageParam: (lastGroup, allPages) => {
        if (lastGroup.items.length === 0 && lastGroup.activities.length === 0) {
          return undefined;
        }

        const nextParam: ActivityPageParam = {
          lastAuditEventDate: lastGroup.lastAuditEventDate,
          lastAuditEventId: lastGroup.lastAuditEventId,
          lastManualEventDate: lastGroup.lastManualEventDate,
          lastManualEventId: lastGroup.lastManualEventId,
        };

        const hasAuditCursor = nextParam.lastAuditEventDate != null && nextParam.lastAuditEventId != null;
        const hasManualCursor = nextParam.lastManualEventDate != null && nextParam.lastManualEventId != null;
        if (!hasAuditCursor && !hasManualCursor) {
          return undefined;
        }

        if (allPages.length >= 2) {
          const previousGroup = allPages[allPages.length - 2];
          const cursorsUnchanged =
            previousGroup.lastAuditEventDate === nextParam.lastAuditEventDate
            && previousGroup.lastAuditEventId === nextParam.lastAuditEventId
            && previousGroup.lastManualEventDate === nextParam.lastManualEventDate
            && previousGroup.lastManualEventId === nextParam.lastManualEventId;
          if (cursorsUnchanged) {
            return undefined;
          }
        }

        return nextParam;
      },

      cacheTime: 0,
      enabled: filtersReady,
    },
  );
};
